import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { isSafeDataText, type DesignDataEntry, type DesignDataField, type DesignDataValue } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import ItemImageField from './ItemImageField';
import './itemsPanel.css';

const SKIPPED_FIELDS = new Set([
  'id',
  'c1',
  'c2',
  'grad',
  'colors',
  'color',
  'palette',
  'icon',
  'art',
  'cover',
  'img',
  'kick',
  'snare',
  'hat',
  'bass',
  'secret'
]);

const TEXTAREA_KEYS = new Set(['blurb', 'desc', 'description', 'quote', 'contents', 'notes']);

function splitWords(input: string): string[] {
  const spaced = input
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ');
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (word.length === 1 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()));
}

function humanizeDataName(name: string): string {
  const words = splitWords(name);
  const lastWord = words.length > 0 ? words[words.length - 1] : undefined;
  if (lastWord !== undefined && /^(Data|Map|Items)$/i.test(lastWord)) {
    words.pop();
  }
  return words.join(' ');
}

function humanizeKey(key: string): string {
  return splitWords(key).join(' ');
}

function truncate(value: string, max = 40): string {
  return value.length > max ? `${value.slice(0, max)}\u2026` : value;
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function getArrayItems(entry: Extract<DesignDataEntry, { kind: 'array' }>, override: unknown): Array<Record<string, DesignDataValue>> {
  if (Array.isArray(override)) return override as Array<Record<string, DesignDataValue>>;
  return entry.items;
}

function isMapValue(value: unknown): value is Record<string, string | number | boolean> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeMap(base: Record<string, unknown>, override: Record<string, unknown>): Record<string, unknown> {
  const result = { ...base };
  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = base[key];
    if (isMapValue(baseValue) && isMapValue(overrideValue)) {
      result[key] = mergeMap(baseValue, overrideValue);
    } else if (overrideValue !== undefined) {
      result[key] = overrideValue;
    }
  }
  return result;
}

function getMapValue(entry: Extract<DesignDataEntry, { kind: 'map' }>, override: unknown): Record<string, unknown> {
  const over = isMapValue(override) ? override : {};
  return mergeMap(entry.value as Record<string, unknown>, over);
}

function setInMap(obj: Record<string, unknown>, path: string[], value: unknown): Record<string, unknown> {
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    if (key === undefined) return next;
    const existing = cur[key] as Record<string, unknown> | undefined;
    cur[key] = { ...(existing ?? {}) };
    cur = cur[key] as Record<string, unknown>;
  }
  const leafKey = path[path.length - 1];
  if (leafKey === undefined) return next;
  cur[leafKey] = value;
  return next;
}

function makeUniqueId(
  items: Array<Record<string, DesignDataValue>>,
  field: DesignDataField
): DesignDataValue {
  if (field.type === 'number') {
    const nums = items
      .map((item) => Number(item[field.key]))
      .filter((n) => Number.isFinite(n));
    return nums.length > 0 ? Math.max(...nums) + 1 : 1;
  }
  if (field.type === 'string') {
    const last = items[items.length - 1]?.[field.key];
    const old = typeof last === 'string' ? last : '';
    let n = 1;
    let candidate = `${old}-${n}`;
    while (items.some((item) => String(item[field.key]) === candidate)) {
      n += 1;
      candidate = `${old}-${n}`;
    }
    return candidate;
  }
  return items[items.length - 1]?.[field.key] ?? '';
}

function makeNewItem(
  template: Record<string, DesignDataValue> | undefined,
  fields: DesignDataField[],
  currentItems: Array<Record<string, DesignDataValue>>
): Record<string, DesignDataValue> {
  const base = template ?? {};
  const newItem: Record<string, DesignDataValue> = { ...base };
  const firstStringField = fields.find((f) => f.type === 'string' && !SKIPPED_FIELDS.has(f.key));
  for (const field of fields) {
    const key = field.key;
    if (key === 'id') {
      newItem[key] = makeUniqueId(currentItems, field);
    } else if (SKIPPED_FIELDS.has(key)) {
      // keep the original value untouched
    } else if (field.type === 'string') {
      newItem[key] = key === firstStringField?.key ? 'New item' : '';
    } else if (field.type === 'number') {
      newItem[key] = 0;
    } else if (field.type === 'boolean') {
      newItem[key] = false;
    } else if (field.type === 'list') {
      newItem[key] = [];
    } else if (field.type === 'image') {
      newItem[key] = '';
    }
  }
  return newItem;
}

interface DataEntryGroupProps {
  dataEntry: DesignDataEntry;
  ctx: PanelCtx;
  openByDefault: boolean;
  drafts: Record<string, string>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  errors: Record<string, string | null>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  updateData: (name: string, next: unknown) => void;
  scheduleReload: () => void;
}

function DataEntryGroup({
  dataEntry,
  ctx,
  openByDefault,
  drafts,
  setDrafts,
  errors,
  setErrors,
  updateData,
  scheduleReload
}: DataEntryGroupProps) {
  const override = ctx.props.data?.[dataEntry.name];
  const isOverridden = override !== undefined;
  const title = humanizeDataName(dataEntry.name);
  const itemCount =
    dataEntry.kind === 'array'
      ? Array.isArray(override)
        ? (override as Array<Record<string, DesignDataValue>>).length
        : dataEntry.items.length
      : undefined;

  const handleReset = () => {
    const nextData = { ...(ctx.props.data ?? {}) };
    delete nextData[dataEntry.name];
    ctx.update({ data: nextData });
    scheduleReload();
  };

  return (
    <details className="dwi-group" data-dwi-group={dataEntry.name} open={openByDefault}>
      <summary className="dwi-group-summary">
        <span className="dwi-group-title">{title}</span>
        {itemCount !== undefined ? <span className="dwi-badge">{itemCount}</span> : null}
        {isOverridden ? <span className="dwi-badge dwi-badge--edited">Edited</span> : null}
        {isOverridden ? (
          <button
            type="button"
            className="dwi-reset-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleReset();
            }}
            aria-label={`Reset ${title}`}
          >
            <Icon name="undo" size={14} />
            <span>Reset</span>
          </button>
        ) : null}
      </summary>
      <div className="dwi-group-body">
        {dataEntry.kind === 'array' ? (
          <ArrayGroup
            ctx={ctx}
            entryName={dataEntry.name}
            fields={dataEntry.fields}
            items={getArrayItems(dataEntry, override)}
            drafts={drafts}
            setDrafts={setDrafts}
            errors={errors}
            setErrors={setErrors}
            updateData={updateData}
          />
        ) : (
          <MapGroup
            entryName={dataEntry.name}
            mapValue={getMapValue(dataEntry, override)}
            drafts={drafts}
            setDrafts={setDrafts}
            errors={errors}
            setErrors={setErrors}
            updateData={updateData}
          />
        )}
      </div>
    </details>
  );
}

interface ArrayGroupProps {
  ctx: PanelCtx;
  entryName: string;
  fields: DesignDataField[];
  items: Array<Record<string, DesignDataValue>>;
  drafts: Record<string, string>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  errors: Record<string, string | null>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  updateData: (name: string, next: unknown) => void;
}

function ArrayGroup({
  ctx,
  entryName,
  fields,
  items,
  drafts,
  setDrafts,
  errors,
  setErrors,
  updateData
}: ArrayGroupProps) {
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const confirmTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (confirmTimerRef.current !== null) window.clearTimeout(confirmTimerRef.current);
    },
    []
  );

  const commitField = useCallback(
    (itemIndex: number, fieldKey: string, value: DesignDataValue) => {
      const nextItems = items.map((item, i) => (i === itemIndex ? { ...item, [fieldKey]: value } : item));
      updateData(entryName, nextItems);
    },
    [items, entryName, updateData]
  );

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...items];
    const prev = next[index - 1];
    const current = next[index];
    if (prev === undefined || current === undefined) return;
    next[index - 1] = current;
    next[index] = prev;
    updateData(entryName, next);
  };

  const moveDown = (index: number) => {
    if (index === items.length - 1) return;
    const next = [...items];
    const current = next[index];
    const following = next[index + 1];
    if (current === undefined || following === undefined) return;
    next[index] = following;
    next[index + 1] = current;
    updateData(entryName, next);
  };

  const duplicate = (index: number) => {
    const copy = { ...items[index] };
    for (const field of fields) {
      if (field.key === 'id') {
        copy[field.key] = makeUniqueId(items, field);
      }
    }
    const next = [...items];
    next.splice(index + 1, 0, copy);
    updateData(entryName, next);
  };

  const handleDeleteClick = (index: number) => {
    if (confirmDeleteIndex === index) {
      if (confirmTimerRef.current !== null) window.clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
      setConfirmDeleteIndex(null);
      const next = items.filter((_, i) => i !== index);
      updateData(entryName, next);
    } else {
      if (confirmTimerRef.current !== null) window.clearTimeout(confirmTimerRef.current);
      setConfirmDeleteIndex(index);
      confirmTimerRef.current = window.setTimeout(() => {
        confirmTimerRef.current = null;
        setConfirmDeleteIndex(null);
      }, 3000);
    }
  };

  const addItem = () => {
    if (items.length >= 60) return;
    const template = items.length > 0 ? items[items.length - 1] : undefined;
    const newItem = makeNewItem(template, fields, items);
    updateData(entryName, [...items, newItem]);
  };

  const canDelete = items.length > 1;
  const atMax = items.length >= 60;

  const isTextField = (f: { key: string; type: string }) => f.type === 'string' && !SKIPPED_FIELDS.has(f.key);
  // Prefer a well-known name field; otherwise label rows by their first text field (e.g. term dates: "Autumn").
  const titleField =
    fields.find((f) => f.type === 'string' && ['name', 'title', 'label', 'author', 'quote', 'day', 'id'].includes(f.key)) ??
    fields.find(isTextField);
  const numberField = fields.find((f) => f.type === 'number' && !SKIPPED_FIELDS.has(f.key));
  // With no number to show, use the next text field as a subtitle so similar rows can be told apart ("First half").
  const subtitleField = numberField ? undefined : fields.find((f) => isTextField(f) && f !== titleField);

  return (
    <div className="dwi-array">
      {items.map((item, itemIndex) => {
        const titleValue = titleField ? String(item[titleField.key] ?? 'Item') : 'Item';
        const subtitleText = subtitleField ? String(item[subtitleField.key] ?? '').trim() : '';
        const secondary = numberField ? formatNumber(Number(item[numberField.key]) || 0) : subtitleText ? truncate(subtitleText, 28) : null;
        const isConfirm = confirmDeleteIndex === itemIndex;

        return (
          <details className="dwi-item" data-dwi-item={itemIndex} key={`${entryName}-${itemIndex}`}>
            <summary className="dwi-item-summary">
              <div className="dwi-item-heading-text">
                <span className="dwi-item-title">{truncate(titleValue)}</span>
                {secondary ? <span className="dwi-item-subtitle">{secondary}</span> : null}
              </div>
              <span className="dwi-item-actions">
                <button
                  type="button"
                  className="dwi-icon-button"
                  aria-label={`Move up ${titleValue}`}
                  disabled={itemIndex === 0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    moveUp(itemIndex);
                  }}
                >
                  <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                    <Icon name="chevron-down" size={15} />
                  </span>
                </button>
                <button
                  type="button"
                  className="dwi-icon-button"
                  aria-label={`Move down ${titleValue}`}
                  disabled={itemIndex === items.length - 1}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    moveDown(itemIndex);
                  }}
                >
                  <Icon name="chevron-down" size={15} />
                </button>
                <button
                  type="button"
                  className="dwi-icon-button"
                  aria-label={`Duplicate ${titleValue}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    duplicate(itemIndex);
                  }}
                >
                  <Icon name="copy" size={15} />
                </button>
                <button
                  type="button"
                  className={`dwi-icon-button dwi-icon-button--danger${isConfirm ? ' dwi-icon-button--confirm' : ''}`}
                  aria-label={isConfirm ? 'Confirm delete' : `Delete ${titleValue}`}
                  disabled={!canDelete}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteClick(itemIndex);
                  }}
                >
                  {isConfirm ? <span>Confirm delete</span> : <Icon name="trash" size={15} />}
                </button>
              </span>
            </summary>
            <div className="dwi-item-body">
              {fields.map((field) => {
                if (SKIPPED_FIELDS.has(field.key)) return null;
                const rawValue = item[field.key];
                const draftKey = `${entryName}::${itemIndex}::${field.key}`;
                const error = errors[draftKey] ?? null;

                if (field.type === 'image') {
                  return (
                    <ItemImageField
                      key={field.key}
                      label={field.key === 'photo' ? 'Picture' : humanizeKey(field.key)}
                      assetId={typeof rawValue === 'string' ? rawValue : ''}
                      ctx={ctx}
                      onChange={(assetId) => commitField(itemIndex, field.key, assetId)}
                    />
                  );
                }

                if (field.type === 'boolean') {
                  return (
                    <div className="dwi-field" key={field.key}>
                      <label className="dw-label" htmlFor={`dwi-${entryName}-${itemIndex}-${field.key}`}>
                        {humanizeKey(field.key)}
                      </label>
                      <input
                        id={`dwi-${entryName}-${itemIndex}-${field.key}`}
                        type="checkbox"
                        checked={rawValue === true}
                        onChange={(e) => commitField(itemIndex, field.key, e.target.checked)}
                      />
                    </div>
                  );
                }

                if (field.type === 'list') {
                  const rawList = Array.isArray(rawValue) ? rawValue : [];
                  const localValue = drafts[draftKey] ?? rawList.join(', ');
                  return (
                    <div className="dwi-field" key={field.key}>
                      <label className="dw-label" htmlFor={`dwi-${entryName}-${itemIndex}-${field.key}`}>
                        {humanizeKey(field.key)}
                      </label>
                      <input
                        id={`dwi-${entryName}-${itemIndex}-${field.key}`}
                        className={`dw-input${error ? ' dwi-invalid' : ''}`}
                        type="text"
                        value={localValue}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          setDrafts((prev) => ({ ...prev, [draftKey]: val }));
                          const parts = val.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
                          if (parts.length > 12) {
                            setErrors((prev) => ({ ...prev, [draftKey]: 'Use up to 12 entries.' }));
                          } else if (!parts.every((s) => isSafeDataText(s, 80))) {
                            setErrors((prev) => ({ ...prev, [draftKey]: 'Plain text only: please avoid < > and " characters' }));
                          } else {
                            setErrors((prev) => ({ ...prev, [draftKey]: null }));
                            commitField(itemIndex, field.key, parts);
                          }
                        }}
                        aria-invalid={error ? true : undefined}
                      />
                      {error ? (
                        <p className="dwi-error" role="alert">
                          {error}
                        </p>
                      ) : null}
                    </div>
                  );
                }

                if (field.type === 'number') {
                  const localValue = drafts[draftKey] ?? String(rawValue ?? 0);
                  return (
                    <div className="dwi-field" key={field.key}>
                      <label className="dw-label" htmlFor={`dwi-${entryName}-${itemIndex}-${field.key}`}>
                        {humanizeKey(field.key)}
                      </label>
                      <input
                        id={`dwi-${entryName}-${itemIndex}-${field.key}`}
                        className={`dw-input${error ? ' dwi-invalid' : ''}`}
                        type="number"
                        step="any"
                        value={localValue}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          setDrafts((prev) => ({ ...prev, [draftKey]: val }));
                          if (val.trim() === '') {
                            setErrors((prev) => ({ ...prev, [draftKey]: null }));
                            return;
                          }
                          const num = Number(val);
                          if (Number.isFinite(num) && Math.abs(num) < 1e9) {
                            setErrors((prev) => ({ ...prev, [draftKey]: null }));
                            commitField(itemIndex, field.key, num);
                          } else {
                            setErrors((prev) => ({ ...prev, [draftKey]: 'Enter a number smaller than 1 billion.' }));
                          }
                        }}
                        onBlur={() => {
                          const val = drafts[draftKey];
                          if (val === undefined || val.trim() === '') {
                            setDrafts((prev) => ({ ...prev, [draftKey]: '0' }));
                            setErrors((prev) => ({ ...prev, [draftKey]: null }));
                            commitField(itemIndex, field.key, 0);
                          } else if (!errors[draftKey]) {
                            const num = Number(val);
                            if (Number.isFinite(num)) {
                              setDrafts((prev) => ({ ...prev, [draftKey]: String(num) }));
                            }
                          }
                        }}
                        aria-invalid={error ? true : undefined}
                      />
                      {error ? (
                        <p className="dwi-error" role="alert">
                          {error}
                        </p>
                      ) : null}
                    </div>
                  );
                }

                // string
                const rawString = typeof rawValue === 'string' ? rawValue : '';
                const localValue = drafts[draftKey] ?? rawString;
                const useTextarea = TEXTAREA_KEYS.has(field.key) || rawString.length > 60;
                const inputId = `dwi-${entryName}-${itemIndex}-${field.key}`;
                return (
                  <div className="dwi-field" key={field.key}>
                    <label className="dw-label" htmlFor={inputId}>
                      {humanizeKey(field.key)}
                    </label>
                    {useTextarea ? (
                      <textarea
                        id={inputId}
                        className={`dw-textarea${error ? ' dwi-invalid' : ''}`}
                        rows={3}
                        value={localValue}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                          const val = e.target.value;
                          setDrafts((prev) => ({ ...prev, [draftKey]: val }));
                          if (isSafeDataText(val, 600)) {
                            setErrors((prev) => ({ ...prev, [draftKey]: null }));
                            commitField(itemIndex, field.key, val);
                          } else {
                            setErrors((prev) => ({ ...prev, [draftKey]: 'Plain text only: please avoid < > and " characters' }));
                          }
                        }}
                        aria-invalid={error ? true : undefined}
                      />
                    ) : (
                      <input
                        id={inputId}
                        className={`dw-input${error ? ' dwi-invalid' : ''}`}
                        type="text"
                        value={localValue}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          setDrafts((prev) => ({ ...prev, [draftKey]: val }));
                          if (isSafeDataText(val, 600)) {
                            setErrors((prev) => ({ ...prev, [draftKey]: null }));
                            commitField(itemIndex, field.key, val);
                          } else {
                            setErrors((prev) => ({ ...prev, [draftKey]: 'Plain text only: please avoid < > and " characters' }));
                          }
                        }}
                        aria-invalid={error ? true : undefined}
                      />
                    )}
                    {error ? (
                      <p className="dwi-error" role="alert">
                        {error}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </details>
        );
      })}
      <div className="dwi-array-footer">
        <button type="button" className="dw-button dw-button--primary" onClick={addItem} disabled={atMax}>
          <Icon name="plus" size={16} />
          <span>Add item</span>
        </button>
        {atMax ? <p className="dw-help-text">This list can hold up to 60 items.</p> : null}
      </div>
    </div>
  );
}

interface MapGroupProps {
  entryName: string;
  mapValue: Record<string, unknown>;
  drafts: Record<string, string>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  errors: Record<string, string | null>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  updateData: (name: string, next: unknown) => void;
}

function MapGroup({
  entryName,
  mapValue,
  drafts,
  setDrafts,
  errors,
  setErrors,
  updateData
}: MapGroupProps) {
  const commitLeaf = useCallback(
    (path: string[], value: unknown) => {
      const nextMap = setInMap(mapValue, path, value);
      updateData(entryName, nextMap);
    },
    [mapValue, entryName, updateData]
  );

  const renderLeaf = (path: string[], value: unknown, depth: number): JSX.Element => {
    const leafKey = `${entryName}::map::${path.join('.')}`;
    const lastSegment = path[path.length - 1];
    const label = lastSegment === undefined ? '' : humanizeKey(lastSegment);
    const inputId = `dwi-map-${entryName}-${path.join('-')}`;

    if (isMapValue(value)) {
      return (
        <div className="dwi-map-sub" style={{ paddingLeft: depth * 12 }} key={path.join('.')}>
          <span className="dwi-map-label">{label}</span>
          <div className="dwi-map-leaf-list">
            {Object.entries(value).map(([k, v]) => renderLeaf([...path, k], v, depth + 1))}
          </div>
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <div className="dwi-map-row" style={{ paddingLeft: depth * 12 }} key={path.join('.')}>
          <span className="dwi-map-label">{label}</span>
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => commitLeaf(path, e.target.checked)}
            aria-label={label}
          />
        </div>
      );
    }

    if (typeof value === 'number') {
      const localValue = drafts[leafKey] ?? String(value);
      const error = errors[leafKey] ?? null;
      return (
        <div className="dwi-map-row" style={{ paddingLeft: depth * 12 }} key={path.join('.')}>
          <span className="dwi-map-label">{label}</span>
          <input
            id={inputId}
            className={`dw-input${error ? ' dwi-invalid' : ''}`}
            type="number"
            step="any"
            value={localValue}
            aria-label={label}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              setDrafts((prev) => ({ ...prev, [leafKey]: val }));
              if (val.trim() === '') {
                setErrors((prev) => ({ ...prev, [leafKey]: null }));
                return;
              }
              const num = Number(val);
              if (Number.isFinite(num) && Math.abs(num) < 1e9) {
                setErrors((prev) => ({ ...prev, [leafKey]: null }));
                commitLeaf(path, num);
              } else {
                setErrors((prev) => ({ ...prev, [leafKey]: 'Enter a number smaller than 1 billion.' }));
              }
            }}
            onBlur={() => {
              const val = drafts[leafKey];
              if (val === undefined || val.trim() === '') {
                setDrafts((prev) => ({ ...prev, [leafKey]: '0' }));
                setErrors((prev) => ({ ...prev, [leafKey]: null }));
                commitLeaf(path, 0);
              } else if (!errors[leafKey]) {
                const num = Number(val);
                if (Number.isFinite(num)) {
                  setDrafts((prev) => ({ ...prev, [leafKey]: String(num) }));
                }
              }
            }}
            aria-invalid={error ? true : undefined}
          />
          {error ? (
            <p className="dwi-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      );
    }

    // string or fallback
    const rawString = typeof value === 'string' ? value : '';
    const localValue = drafts[leafKey] ?? rawString;
    const error = errors[leafKey] ?? null;
    return (
      <div className="dwi-map-row" style={{ paddingLeft: depth * 12 }} key={path.join('.')}>
        <span className="dwi-map-label">{label}</span>
        <input
          id={inputId}
          className={`dw-input${error ? ' dwi-invalid' : ''}`}
          type="text"
          value={localValue}
          aria-label={label}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setDrafts((prev) => ({ ...prev, [leafKey]: val }));
            if (isSafeDataText(val, 600)) {
              setErrors((prev) => ({ ...prev, [leafKey]: null }));
              commitLeaf(path, val);
            } else {
              setErrors((prev) => ({ ...prev, [leafKey]: 'Plain text only: please avoid < > and " characters' }));
            }
          }}
          aria-invalid={error ? true : undefined}
        />
        {error ? (
          <p className="dwi-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="dwi-map">
      {Object.entries(mapValue).map(([key, value]) => renderLeaf([key], value, 0))}
    </div>
  );
}

export default function ItemsPanel({ ctx }: { ctx: PanelCtx }) {
  const reloadTimerRef = useRef<number | null>(null);
  const [reloadPending, setReloadPending] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current !== null) window.clearTimeout(reloadTimerRef.current);
    setReloadPending(true);
    reloadTimerRef.current = window.setTimeout(() => {
      reloadTimerRef.current = null;
      setReloadPending(false);
      ctx.reload();
    }, 600);
  }, [ctx.reload]);

  useEffect(
    () => () => {
      if (reloadTimerRef.current !== null) window.clearTimeout(reloadTimerRef.current);
    },
    []
  );

  const updateData = useCallback(
    (name: string, next: unknown) => {
      ctx.update({ data: { ...(ctx.props.data ?? {}), [name]: next } });
      scheduleReload();
    },
    [ctx, scheduleReload]
  );

  // A click on a card in the preview selects "LIST:INDEX": open that list and that item, scroll to it and pulse it.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const selectedItem = ctx.selection && ctx.selection.kind === 'item' ? ctx.selection : null;
  useEffect(() => {
    if (!selectedItem) return;
    const sep = selectedItem.id.lastIndexOf(':');
    const listName = selectedItem.id.slice(0, sep);
    const itemIndex = selectedItem.id.slice(sep + 1);
    const timer = window.setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const group = Array.from(root.querySelectorAll<HTMLDetailsElement>('details[data-dwi-group]')).find(
        (el) => el.getAttribute('data-dwi-group') === listName
      );
      if (!group) return;
      group.open = true;
      const item = Array.from(group.querySelectorAll<HTMLDetailsElement>('details[data-dwi-item]')).find(
        (el) => el.getAttribute('data-dwi-item') === itemIndex
      );
      const target = item ?? group;
      if (item) item.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('dw-pulse');
      window.setTimeout(() => target.classList.remove('dw-pulse'), 1400);
    }, 60);
    return () => window.clearTimeout(timer);
  }, [selectedItem?.nonce, selectedItem?.id]);

  const entry = ctx.entry;

  if (!entry.data || entry.data.length === 0) {
    return (
      <div className="dw-panel">
        <div className="dw-panel-heading">
          <Icon name="grid" size={18} />
          <h2>Items & prices</h2>
        </div>
        <div className="dw-empty">
          This design has no script-driven lists. Click any text on the page to edit it.
        </div>
      </div>
    );
  }

  return (
    <div className="dw-panel" ref={panelRef}>
      <div className="dw-panel-heading">
        <Icon name="grid" size={18} />
        <h2>Items & prices</h2>
      </div>
      <p className="dw-help-text">
        Click a card on the page to jump to it here. Edit the lists and prices that power your page: menus, products, rooms, builds and more. The preview refreshes when you stop typing.
      </p>
      {reloadPending ? (
        <div className="dwi-reload-status" role="status" aria-live="polite">
          Refreshing preview...
        </div>
      ) : null}
      <div className="dwi-groups">
        {entry.data.map((dataEntry, index) => (
          <DataEntryGroup
            key={dataEntry.name}
            dataEntry={dataEntry}
            ctx={ctx}
            openByDefault={index === 0}
            drafts={drafts}
            setDrafts={setDrafts}
            errors={errors}
            setErrors={setErrors}
            updateData={updateData}
            scheduleReload={scheduleReload}
          />
        ))}
      </div>
    </div>
  );
}
